import { gql, useMutation } from '@apollo/client';
import { getTranslations } from '@assets/i18n/config';
import {
  IntegrationProviders,
  IntegrationTypes,
  LinkTokenConfig,
  WorkflowActionTypes,
} from '@generated/graphql';
import { NetWorthWorkflowContext } from '@ovComponents/4-module/netWorthWorkflow/netWorthWorkflowContext';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { UserContext } from '@providers/userContextProvider';
import { Box, CircularProgress, Typography, useToast } from 'hephaestus/web';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlaidEmbeddedLink } from 'react-plaid-link';

const CREATE_LINK_TOKEN = gql`
  mutation createLinkToken($input: CreateLinkTokenInput!) {
    createLinkToken(input: $input) {
      linkToken
    }
  }
`;

const FILE_NAME = 'netWorth';

const NetWorthIntegrationLink = ({ onNext }: { onNext: () => void }) => {
  const { t } = useTranslation(FILE_NAME);
  const { enqueueToast } = useToast();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [linkToken, setLinkToken] = useState<string | null>(null);

  const { integrations } = useContext(UserContext);
  const { netWorthData, setNetWorthData } = useContext(NetWorthWorkflowContext);
  const { workflowData, setWorkflowData, userId } = useContext(WorkflowContext);
  const currentUserId = workflowData.currentUserId || userId;

  const applicableData =
    Object.keys(netWorthData).length !== 0 ? netWorthData : workflowData;
  const setApplicationContext =
    Object.keys(netWorthData).length !== 0 ? setNetWorthData : setWorkflowData;

  const findIntegrationCondition = (integration: any) =>
    integration?.type === IntegrationTypes.Banking &&
    ['netWorthAutomaticLinkingUrl', 'netWorthAutomaticLinkingToken'].every(
      (key) => Object.keys(integration?.configuration ?? {}).includes(key),
    );

  const bankingIntegration = integrations?.find(findIntegrationCondition);
  const hasPlaidIntegration =
    bankingIntegration &&
    bankingIntegration.provider === IntegrationProviders.Plaid;
  const configurations = bankingIntegration?.configuration;

  const [createLinkToken] = useMutation(CREATE_LINK_TOKEN, {
    variables: {
      input: {
        userId:
          applicableData.objectId ||
          (applicableData === workflowData
            ? currentUserId
            : applicableData.currentUserId),
        clientId: configurations?.customerId,
        plaidSecret: configurations?.plaidSecret,
        configType: LinkTokenConfig.NetWorth,
      },
    },
  });

  useEffect(() => {
    if (!hasPlaidIntegration) {
      setIsLoading(false);
      return;
    }
    const fetchLinkToken = async () => {
      const token = await createLinkToken();
      setLinkToken(token.data?.createLinkToken?.linkToken ?? null);
      setIsLoading(false);
    };
    fetchLinkToken();
  }, []);

  return (
    <>
      {!isLoading && hasPlaidIntegration && linkToken && (
        <PlaidEmbeddedLink
          token={linkToken}
          onSuccess={(publicToken) => {
            setApplicationContext({
              ...applicableData,
              netWorthData: {
                ...applicableData.netWorthData,
                publicToken: publicToken ?? '',
                netWorthStep: 2,
                netWorthLinkingActionType:
                  WorkflowActionTypes.SyncNetWorthAccountsFromPlaid,
                netWorthLinkingNotificationGroup:
                  configurations?.netWorthAutomaticLinkingNotificationGroup ??
                  '',
                notificationSuccessTitle: getTranslations({
                  fileName: FILE_NAME,
                  translationPath: [
                    'addNetWorthAutomatically',
                    'netWorthAutomaticLink',
                    'notificationSuccessTitle',
                  ],
                }),
                notificationSuccessBody: getTranslations({
                  fileName: FILE_NAME,
                  translationPath: [
                    'addNetWorthAutomatically',
                    'netWorthAutomaticLink',
                    'notificationSuccessBody',
                  ],
                }),
                notificationFailureTitle: getTranslations({
                  fileName: FILE_NAME,
                  translationPath: [
                    'addNetWorthAutomatically',
                    'netWorthAutomaticLink',
                    'notificationFailureTitle',
                  ],
                }),
                notificationFailureBody: getTranslations({
                  fileName: FILE_NAME,
                  translationPath: [
                    'addNetWorthAutomatically',
                    'netWorthAutomaticLink',
                    'notificationFailureBody',
                  ],
                }),
                notificationLinkText: getTranslations({
                  fileName: FILE_NAME,
                  translationPath: [
                    'addNetWorthAutomatically',
                    'netWorthAutomaticLink',
                    'notificationLinkText',
                  ],
                }),
                notificationLink: '#',
              },
            });
            onNext();
          }}
          onExit={(error) => {
            if (error) {
              enqueueToast(t('addNetWorthAutomatically.linkError'), {
                variant: 'error',
              });
            }
          }}
          style={{
            height: '550px',
            width: '550px',
            border: 0,
          }}
        />
      )}
      {!isLoading && (!hasPlaidIntegration || !linkToken) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '550px',
            paddingX: '40px',
          }}
        >
          <Typography variant='labelLarge' sx={{ mb: 3 }}>
            {t('addNetWorthAutomatically.noIntegration')}
          </Typography>
        </Box>
      )}
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '550px',
            paddingX: '40px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: '40px',
            }}
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant='labelLarge' sx={{ mb: 3 }}>
              {t('addNetWorthAutomatically.linkingTimeInfo')}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default NetWorthIntegrationLink;
